import { toDate } from '@/util/date';
import {
  addSeconds,
  differenceInSeconds,
  formatISO,
  isEqual,
  startOfMinute,
} from 'date-fns';
import type { DateRange, PresetOption } from './types';

export function toInputFormat(date?: Date) {
  if (!date) return '';
  return formatISO(startOfMinute(date)).substring(0, 16);
}

export function limitRange(
  previousRange: string[],
  newRange: string[],
  max?: number | undefined
) {
  if (!max) {
    return newRange;
  }

  const range = {
    from: toDate(newRange[0]),
    to: toDate(newRange[1]),
  };
  const diff = differenceInSeconds(range.to, range.from);

  if (diff <= max) {
    return newRange;
  }

  const changed = {
    from: previousRange[0] !== newRange[0],
    to: previousRange[1] !== newRange[1],
  };
  const initial = !changed.from && !changed.to;

  let { from, to } = range;
  if (changed.to) {
    from = addSeconds(range.to, -max);
  }
  if (changed.from || initial) {
    to = addSeconds(range.from, max);
  }

  return [toInputFormat(from), toInputFormat(to)];
}

export function isDateRange(value: any): value is DateRange {
  return Object.hasOwn(value, 'from');
}

export type DateModifier = (date: Date | number) => Date;

interface GetRangeOptions {
  modifier?: DateModifier;
}

export function getRange(
  value: string[] | DateRange,
  {
    modifier = (date) => (typeof date === 'number' ? new Date(date) : date),
  }: GetRangeOptions = {}
) {
  if (isDateRange(value)) {
    return { from: modifier(value.from), to: modifier(value.to) };
  }

  const from = Date.parse(value[0]);
  const to = Date.parse(value[1]);
  if (isNaN(from) || isNaN(to)) {
    return;
  }
  return { from: modifier(from), to: modifier(to) };
}

export function isDateRangeEqual(
  dateRangeLeft: DateRange,
  dateRangeRight: DateRange
) {
  return (
    isEqual(dateRangeLeft.from, dateRangeRight.from) &&
    isEqual(dateRangeLeft.to, dateRangeRight.to)
  );
}

export function isPresetOption(value: any): value is PresetOption {
  return value && value.value && value.label && value.fn;
}
